import { FormControl, FormHelperText, InputBase, InputLabel, MenuItem, Select, styled } from "@mui/material";
import { certificateValidityStatusIds } from "lib/helper/certificateValidityStatusIds";
import { getSortedOptions, LabelArray } from "lib/helper/getSortedOptions";
import { getTranslatedCertificateValidityStatusString } from "lib/helper/getTranslatedRowDataStrings";
import {
  certificateToEditIdAtom,
  isCertificateToReplaceOperationAtom,
  isUploadValidExtendedByAtom,
} from "pages/UploadCertificate/state/uploadCertificateStateBase";
import { useGetUserInfo } from "lib/userInfo";
import React, { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import ordinaryColors from "themes/ordinaryColors";
import Props from "./index.d";
import useGetForeignKeyList from "./useGetForeignKeyList";

export const StyledInputBase = styled(InputBase)<{ error?: boolean; isEnumSelector?: boolean }>(
  ({ error, isEnumSelector }) => ({
    maxHeight: "42px",
    minHeight: "42px",
    display: "flex",
    padding: "8px 12px",
    alignContent: "center",
    rowGap: "100px",
    flex: "1 0 0",
    flexWrap: "wrap",
    borderRadius: "0px",
    border: "1px solid ",
    borderColor: error ? ordinaryColors.error : ordinaryColors.border,
    backgroundColor: error ? "rgba(211, 47, 47, 0.02)" : "transparent",
    marginBottom: "0px",
  })
);

// Allowed target statuses when editing a certificate, keyed by the certificate's current status.
// An empty array means no status change is permitted.
const EDIT_STATUS_TRANSITIONS: Record<string, string[]> = {
  [certificateValidityStatusIds.Valid]: [
    certificateValidityStatusIds.Expired,
    certificateValidityStatusIds.WithdrawnNotReplaced,
  ],
  [certificateValidityStatusIds.Expired]: [certificateValidityStatusIds.WithdrawnNotReplaced],
  [certificateValidityStatusIds.ValidExtendedBy]: [certificateValidityStatusIds.WithdrawnNotReplaced],
  [certificateValidityStatusIds.WithdrawnNotReplaced]: [],
  [certificateValidityStatusIds.WithdrawnReplacedBy]: [],
  [certificateValidityStatusIds.Void]: [],
};

const STATUS_OPTIONS_FOR_UPLOAD: string[] = [certificateValidityStatusIds.Valid, certificateValidityStatusIds.Expired];

const ForeignKeySelectorStatus: React.FC<Props> = (props) => {
  const {
    data: { state, setstate, updateValue, onBlur },
    title,
    pickDefaultFirst,
    className,
    foreignKeyParams,
    hideLabel,
    errorManager,
    isRequiredField,
    isUnit,
    testId,
  } = props;
  let elems = useGetForeignKeyList({ foreignKeyParams });

  useEffect(() => {
    if (pickDefaultFirst && !state && !!elems && elems.length) {
      setstate(elems[0].value);
    }
  }, [elems, state, pickDefaultFirst, setstate]);
  const isUploadValidExtendedBy = useRecoilValue(isUploadValidExtendedByAtom);
  const isCertificateToEdit = useRecoilValue(certificateToEditIdAtom);
  const isCertificateToReplaceIdOperation = useRecoilValue(isCertificateToReplaceOperationAtom);
  const userInfo = useGetUserInfo();
  const isPlatformOperator = userInfo?.role === "PLATFORM_OPERATOR";
  // Captures the certificate's original status when the edit form first opens.
  // Must not be overwritten on every re-render so the transition map stays anchored to the persisted value.
  const originalStatusRef = useRef<string | undefined>();
  // Handle state updates when certificate operation type changes
  useEffect(() => {
    if (isUploadValidExtendedBy) {
      // If extended validity flow, force "Valid (extended by)" status
      setstate(certificateValidityStatusIds.ValidExtendedBy);
    }

    // Capture the original status the first time the edit form opens (only set once)
    if (isCertificateToEdit && state && originalStatusRef.current === undefined) {
      originalStatusRef.current = state;
    }
    if (isCertificateToReplaceIdOperation) {
      // If replace operation flow, ensure state is one of the valid options and per default its "valid"
      const isStateValid = state && STATUS_OPTIONS_FOR_UPLOAD.includes(state);

      if (!isStateValid) {
        setstate(certificateValidityStatusIds.Valid);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCertificateToReplaceIdOperation, isUploadValidExtendedBy, state, setstate]);
  // The status the certificate had when the edit form was opened
  const originalStatus = originalStatusRef.current;
  // No transitions available → disable the dropdown entirely (PO is always exempt)
  const isStatusChangeDisabled =
    !isPlatformOperator &&
    !!isCertificateToEdit &&
    !!originalStatus &&
    EDIT_STATUS_TRANSITIONS[originalStatus]?.length === 0;

  const errorManagerRes = errorManager({
    errorJoiner: (errors) => {
      return errors.join(", ");
    },
  });

  if (foreignKeyParams.entityName === "certificateValidityStatuses")
    elems = getSortedOptions({
      labelArray: elems,
      sortFn: getTranslatedCertificateValidityStatusString,
    }) as LabelArray;
  return (
    <>
      <FormControl fullWidth className={className}>
        {!isUnit || (hideLabel && <InputLabel id="demo-simple-select-label">{title}</InputLabel>)}
        <Select
          data-testid={testId}
          labelId="demo-simple-select-label"
          id="demo-simple-select"
          input={<StyledInputBase {...errorManagerRes} />}
          value={state ?? ""}
          label={hideLabel === true ? undefined : title}
          onBlur={onBlur}
          onChange={updateValue}
          required={isRequiredField}
          //disabled when valid extended by or no status transitions are available
          disabled={isUploadValidExtendedBy || isStatusChangeDisabled}
          {...errorManagerRes}
        >
          {elems
            .filter((e) => {
              // Always include the current (selected) state so the dropdown is never empty
              if (state === e.value) {
                return true;
              }

              // In edit mode: show all statuses (disabled ones handled in MenuItem)
              if (isCertificateToEdit) {
                return true;
              }

              // Extended validity upload: only show "Valid (extended by)"
              if (isUploadValidExtendedBy) {
                return e.value === certificateValidityStatusIds.ValidExtendedBy;
              }

              // New upload or replace operation: only Valid and Expired
              return STATUS_OPTIONS_FOR_UPLOAD.includes(e.value);
            })
            .map(({ value, label }) => {
              const allowedTargets = EDIT_STATUS_TRANSITIONS[originalStatus ?? ""] ?? [];
              const isDisabledInEdit =
                !isPlatformOperator &&
                !!isCertificateToEdit &&
                value !== originalStatus &&
                !allowedTargets.includes(value);
              return (
                <MenuItem key={value} value={value} disabled={isDisabledInEdit}>
                  {foreignKeyParams.entityName !== "certificateValidityStatuses"
                    ? label
                    : getTranslatedCertificateValidityStatusString(value)}
                </MenuItem>
              );
            })}
        </Select>
        {errorManagerRes.error && !isUnit && (
          <FormHelperText sx={{ color: ordinaryColors.error, marginLeft: "0px", fontSize: "12px" }}>
            {errorManagerRes.helperText}
          </FormHelperText>
        )}
      </FormControl>
    </>
  );
};

export default ForeignKeySelectorStatus;
